<template>
  <div class="w-full">
    <PortalCard
      v-if="!content.occurred"
      :content="content"
      :characters="characters"
      :isDone="isDone"
      :class="[animation ? 'portal-animation' : '']"
      @clickCard="handleClick"
    />
  </div>
</template>

<script lang="ts">
import { Content, Sequence } from '@forchange/storytime';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

import SequenceQueue from '@/utils/portal/SequenceQueue';
import PortalCard from './PortalCard.vue';
import { namespace } from 'vuex-class';
import { PortalMessage } from '@/store/modules/common/types';

const userModule = namespace('common');

@Component({
  components: {
    PortalCard
  }
})
export default class Portal extends Vue {
  @Prop({ type: Object }) content!: Content & {
    path: string;
    desc: string;
    occurred: boolean;
    name: string;
    autoJump: boolean;
    portalSequence: Sequence;
  };
  @Prop({ type: Array }) characters!: Array<{
    name: string;
    avatar: string;
    direction?: 'right' | 'left';
  }>;

  @userModule.State('portalMessage') portalMessage!: PortalMessage;

  get animation() {
    return this.portalMessage.animation && this.content.$id === window.storyteller.topContet?.$id;
  }

  get isDone() {
    return !!this.content.portalSequence;
  }

  @Watch('portalMessage')
  portalMessageChange() {
    if (this.portalMessage.animation) {
      setTimeout(() => {
        this.$store.commit('common/setPortalMessage', {
          animation: false
        });
      }, 1000);
    }
  }

  mounted() {
    if (!['是', 'true'].includes(this.content.autoJump?.toString())) return;
    // 兼容含有PortalEnd的情况
    if (this.content.occurred) return this.setPortalEnd();
    this.$nextTick(() => {
      if (this.content.portalSequence) return;
      this.initNextNode(true);
    });
  }

  initNextNode(autoJump?: boolean) {
    window.sequenceStack.push(new SequenceQueue(this.content.$id));
    const isSuccess = window.storyteller.call({
      path: this.content.path,
      notAotuCall: true,
      prop: { scrollTop: (this.$parent?.$refs?.scroller as HTMLElement).scrollTop, autoJump }
    });
    if (!isSuccess) {
      this.$Message.error({
        content: '没有找到对应的文稿，请检查路径是否正确',
        time: 1500
      });
      return;
    }
    window.next();
  }

  setNode() {
    window.storyteller.createNextNode({
      ...this.content,
      $id: `${this.content.$id}-custom-portal`,
      $kind: 'PortalEnd'
    });
  }

  @Watch('content.$id', {
    immediate: true
  })
  setPortalEnd() {
    const sequenceQueue = window.sequenceStack.peek();
    if (sequenceQueue?.portalId !== this.content.$id) return;
    window.sequenceStack.pop();

    // 兼容历史记录
    if (this.content.occurred) {
      window.storyteller.updateContent((content) => {
        return {
          portalSequence: sequenceQueue?.getFront()
        };
      });

      return;
    }

    // 抛掉portal层级track记录
    const storeTrack = window.storyteller.getTrack();
    storeTrack.trackCallStack.pop();

    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        portalSequence: sequenceQueue?.getFront()
      }
    });
  }

  handleClick() {
    // 如果存在则直接进入
    if (this.content.portalSequence) {
      this.$store.commit('common/setPortal', {
        ...this.content.portalSequence,
        scrollTop: (this.$parent?.$refs?.scroller as HTMLElement).scrollTop
      });
      return;
    }

    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        autoJump: false
      }
    });
    this.$nextTick(() => {
      this.initNextNode();
    });
  }
}
</script>
<style scoped>
.portal-animation {
  animation: shakeX 0.8s;
}

@keyframes shakeX {
  from,
  to {
    transform: translate3d(0, 0, 0);
  }

  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translate3d(-5px, 0, 0);
  }

  20%,
  40%,
  60%,
  80% {
    transform: translate3d(5px, 0, 0);
  }
}
</style>
